import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Pressable,
  TextInput,
  StyleSheet,
  Platform,
  ActivityIndicator,
  Modal,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import WebScrollView from '../components/WebScrollView';
import { Users, Plus, X, Loader2, CheckCircle2, AlertTriangle, Calendar } from '../lib/icons';
import { supabase } from '../lib/supabase';

interface Service {
  id: string;
  name: string;
  provider: string;
  frequency: string;
  last_service_date: string;
  next_service_date: string;
  notes: string;
  status: 'active' | 'paused' | 'cancelled';
}

const serviceTypes = [
  { id: 'landscaping', label: 'Landscaping' },
  { id: 'housekeeping', label: 'Housekeeping' },
  { id: 'pest_control', label: 'Pest Control' },
  { id: 'pool_maintenance', label: 'Pool Maintenance' },
  { id: 'hvac', label: 'HVAC Service' },
  { id: 'plumbing', label: 'Plumbing' },
  { id: 'electrical', label: 'Electrical' },
  { id: 'roofing', label: 'Roofing' },
  { id: 'cleaning', label: 'Deep Cleaning' },
];

const frequencies = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semiannual', label: 'Semi-annual' },
  { value: 'annual', label: 'Annual' },
  { value: 'asneeded', label: 'As Needed' },
];

const getStatusColors = (status: Service['status']) => {
  switch (status) {
    case 'active':
      return { color: '#16a34a', backgroundColor: '#f0fdf4' };
    case 'paused':
      return { color: '#ea580c', backgroundColor: '#fff7ed' };
    case 'cancelled':
      return { color: '#dc2626', backgroundColor: '#fef2f2' };
    default:
      return { color: '#4b5563', backgroundColor: '#f9fafb' };
  }
};

const getServiceLabel = (serviceId: string) =>
  serviceTypes.find((type) => type.id === serviceId)?.label || serviceId;

const getFrequencyLabel = (frequencyValue: string) =>
  frequencies.find((f) => f.value === frequencyValue)?.label || frequencyValue;

const formatDate = (value: string) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString();
};

const Services = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: serviceTypes[0].id,
    provider: '',
    frequency: frequencies[0].value,
    last_service_date: '',
    notes: '',
    status: 'active' as const,
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('managed_services')
        .select('*')
        .order('name', { ascending: true });

      if (fetchError) throw fetchError;
      setServices(data || []);
    } catch (err) {
      console.error('Error fetching services:', err);
    } finally {
      setLoading(false);
    }
  };

  const computeNextServiceDate = (lastServiceDate: string, frequency: string) => {
    const last = new Date(lastServiceDate);
    let next = new Date(last);

    switch (frequency) {
      case 'weekly':
        next.setDate(next.getDate() + 7);
        break;
      case 'biweekly':
        next.setDate(next.getDate() + 14);
        break;
      case 'monthly':
        next.setMonth(next.getMonth() + 1);
        break;
      case 'quarterly':
        next.setMonth(next.getMonth() + 3);
        break;
      case 'semiannual':
        next.setMonth(next.getMonth() + 6);
        break;
      case 'annual':
        next.setFullYear(next.getFullYear() + 1);
        break;
      case 'asneeded':
        next = last;
        break;
    }

    return next;
  };

  const handleSubmit = async () => {
    if (!formData.provider || !formData.last_service_date) {
      setError('Please fill in the provider and last service date.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const nextServiceDate = computeNextServiceDate(formData.last_service_date, formData.frequency);
      const { error: insertError } = await supabase.from('managed_services').insert([
        {
          ...formData,
          next_service_date: nextServiceDate.toISOString().split('T')[0],
        },
      ]);

      if (insertError) throw insertError;

      setFormData({
        name: serviceTypes[0].id,
        provider: '',
        frequency: frequencies[0].value,
        last_service_date: '',
        notes: '',
        status: 'active',
      });
      setIsFormOpen(false);
      fetchServices();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderServiceCard = (service: Service) => {
    const statusColors = getStatusColors(service.status);
    const statusLabel = service.status.charAt(0).toUpperCase() + service.status.slice(1);

    return (
      <View key={service.id} style={styles.card}>
        <View style={styles.cardHeader}>
          <View style={styles.cardHeaderText}>
            <Text style={styles.cardTitle}>{getServiceLabel(service.name)}</Text>
            <Text style={styles.cardSubtitle}>{service.provider}</Text>
          </View>
          <View style={[styles.statusPill, { backgroundColor: statusColors.backgroundColor }]}>
            <Text style={[styles.statusPillText, { color: statusColors.color }]}>{statusLabel}</Text>
          </View>
        </View>

        <View style={styles.cardBody}>
          <View style={styles.cardRow}>
            <Calendar size={16} color="#4b5563" />
            <Text style={styles.cardRowText}>{getFrequencyLabel(service.frequency)}</Text>
          </View>
          <View style={styles.cardMetaRow}>
            <Text style={styles.cardMetaLabel}>Last service:</Text>
            <Text style={styles.cardMetaValue}>{formatDate(service.last_service_date)}</Text>
          </View>
          <View style={styles.cardMetaRow}>
            <Text style={styles.cardMetaLabel}>Next service:</Text>
            <Text style={styles.cardMetaValue}>{formatDate(service.next_service_date)}</Text>
          </View>
        </View>

        {!!service.notes && (
          <View style={styles.cardNotes}>
            <Text style={styles.cardNotesText}>{service.notes}</Text>
          </View>
        )}
      </View>
    );
  };

  const renderDateField = () => {
    if (Platform.OS === 'web') {
      return (
        <input
          type="date"
          value={formData.last_service_date}
          onChange={(e) => setFormData({ ...formData, last_service_date: e.target.value })}
          style={{
            padding: '10px 12px',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: '#d1d5db',
            borderRadius: 8,
            fontSize: 14,
            color: '#111827',
            backgroundColor: '#ffffff',
            width: '100%',
            fontFamily: 'inherit',
            outlineStyle: 'none',
          }}
        />
      );
    }

    return (
      <TextInput
        value={formData.last_service_date}
        onChangeText={(value) => setFormData({ ...formData, last_service_date: value })}
        placeholder="YYYY-MM-DD"
        placeholderTextColor="#9ca3af"
        style={styles.input}
      />
    );
  };

  return (
    <WebScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <View style={styles.header}>
        <View style={styles.headerText}>
          <Text style={styles.title}>Managed Services</Text>
          <Text style={styles.subtitle}>Track and manage your recurring services</Text>
        </View>
        <Pressable style={styles.primaryButton} onPress={() => setIsFormOpen(true)}>
          <Plus size={16} color="#ffffff" />
          <Text style={styles.primaryButtonText}>Add Service</Text>
        </Pressable>
      </View>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color="#9ca3af" />
        </View>
      ) : services.length === 0 ? (
        <View style={styles.emptyState}>
          <Users size={48} color="#9ca3af" />
          <Text style={styles.emptyTitle}>No Services Added</Text>
          <Text style={styles.emptySubtitle}>Start tracking your managed services</Text>
          <Pressable style={styles.primaryButton} onPress={() => setIsFormOpen(true)}>
            <Plus size={16} color="#ffffff" />
            <Text style={styles.primaryButtonText}>Add Your First Service</Text>
          </Pressable>
        </View>
      ) : (
        <View style={styles.cardGrid}>{services.map(renderServiceCard)}</View>
      )}

      <Modal
        visible={isFormOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setIsFormOpen(false)}
      >
        <View style={styles.modalOverlay}>
          <Pressable style={styles.modalBackdrop} onPress={() => setIsFormOpen(false)} />
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Managed Service</Text>
              <Pressable onPress={() => setIsFormOpen(false)} hitSlop={8}>
                <X size={20} color="#9ca3af" />
              </Pressable>
            </View>

            <WebScrollView style={styles.modalBody} contentContainerStyle={styles.modalBodyContent}>
              <View style={styles.formGroup}>
                <Text style={styles.label}>Service Type</Text>
                <View style={styles.pickerContainer}>
                  <Picker
                    selectedValue={formData.name}
                    onValueChange={(value) => setFormData({ ...formData, name: value })}
                    style={styles.picker}
                  >
                    {serviceTypes.map((type) => (
                      <Picker.Item key={type.id} label={type.label} value={type.id} />
                    ))}
                  </Picker>
                </View>
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Service Provider</Text>
                <TextInput
                  value={formData.provider}
                  onChangeText={(value) => setFormData({ ...formData, provider: value })}
                  placeholder="Enter provider name"
                  placeholderTextColor="#9ca3af"
                  style={styles.input}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Service Frequency</Text>
                <View style={styles.pickerContainer}>
                  <Picker
                    selectedValue={formData.frequency}
                    onValueChange={(value) => setFormData({ ...formData, frequency: value })}
                    style={styles.picker}
                  >
                    {frequencies.map((frequency) => (
                      <Picker.Item key={frequency.value} label={frequency.label} value={frequency.value} />
                    ))}
                  </Picker>
                </View>
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Last Service Date</Text>
                {renderDateField()}
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Notes</Text>
                <TextInput
                  value={formData.notes}
                  onChangeText={(value) => setFormData({ ...formData, notes: value })}
                  placeholder="Optional notes about the service"
                  placeholderTextColor="#9ca3af"
                  multiline
                  numberOfLines={3}
                  style={[styles.input, styles.textArea]}
                />
              </View>

              {!!error && (
                <View style={styles.errorContainer}>
                  <AlertTriangle size={16} color="#dc2626" />
                  <Text style={styles.errorText}>{error}</Text>
                </View>
              )}
            </WebScrollView>

            <View style={styles.modalFooter}>
              <Pressable style={styles.secondaryButton} onPress={() => setIsFormOpen(false)}>
                <Text style={styles.secondaryButtonText}>Cancel</Text>
              </Pressable>
              <Pressable
                style={[styles.primaryButton, submitting && styles.primaryButtonDisabled]}
                onPress={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} color="#ffffff" />
                    <Text style={styles.primaryButtonText}>Adding...</Text>
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={16} color="#ffffff" />
                    <Text style={styles.primaryButtonText}>Add Service</Text>
                  </>
                )}
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  contentContainer: {
    padding: 16,
    gap: 24,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  headerText: {
    flexShrink: 1,
    gap: 4,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: '#111827',
  },
  subtitle: {
    fontSize: 14,
    color: '#6b7280',
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#000000',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer' as any,
    }),
  },
  primaryButtonDisabled: {
    opacity: 0.5,
  },
  primaryButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  secondaryButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer' as any,
    }),
  },
  secondaryButtonText: {
    color: '#4b5563',
    fontSize: 14,
    fontWeight: '500',
  },
  loadingContainer: {
    paddingVertical: 48,
    alignItems: 'center',
  },
  emptyState: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 48,
    alignItems: 'center',
    gap: 12,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  emptySubtitle: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 12,
  },
  cardGrid: {
    gap: 16,
    ...(Platform.OS === 'web' && {
      display: 'flex' as any,
      flexDirection: 'row' as any,
      flexWrap: 'wrap' as any,
    }),
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 24,
    gap: 16,
    ...(Platform.OS === 'web' && {
      flexBasis: 320 as any,
      flexGrow: 1 as any,
      maxWidth: '100%' as any,
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)' as any,
    }),
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  cardHeaderText: {
    flexShrink: 1,
    gap: 2,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  cardSubtitle: {
    fontSize: 14,
    color: '#6b7280',
  },
  statusPill: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  statusPillText: {
    fontSize: 12,
    fontWeight: '500',
  },
  cardBody: {
    gap: 8,
  },
  cardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  cardRowText: {
    fontSize: 14,
    color: '#4b5563',
  },
  cardMetaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  cardMetaLabel: {
    fontSize: 14,
    color: '#6b7280',
  },
  cardMetaValue: {
    fontSize: 14,
    color: '#111827',
    fontWeight: '500',
  },
  cardNotes: {
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
    paddingTop: 12,
  },
  cardNotesText: {
    fontSize: 14,
    color: '#6b7280',
  },
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    width: '100%',
    maxWidth: 480,
    maxHeight: '90%',
    overflow: 'hidden',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' as any,
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  modalBody: {
    flexGrow: 0,
    flexShrink: 1,
  },
  modalBodyContent: {
    paddingHorizontal: 24,
    paddingVertical: 20,
    gap: 16,
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
  },
  formGroup: {
    gap: 6,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  input: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 14,
    color: '#111827',
    backgroundColor: '#ffffff',
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
  },
  picker: {
    ...(Platform.OS === 'web' && {
      padding: 10,
      borderWidth: 0,
      backgroundColor: 'transparent',
      fontSize: 14,
    } as any),
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#fef2f2',
    padding: 12,
    borderRadius: 8,
  },
  errorText: {
    color: '#dc2626',
    fontSize: 14,
    flexShrink: 1,
  },
});

export default Services;
